import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Col, Row, Spinner } from 'reactstrap'
import { IAccessory } from './index'
import { toast } from 'react-toastify'
import { getAssetUrl } from '../../../helpers/backend_helper'
import DeleteModal from 'Components/Common/DeleteModal'
import { useDebouncedCallback } from 'use-debounce'

interface IProps {
  fetching: boolean
  accessoryRecords: IAccessory[]
  handleOpenDetails: (arg: string) => void
  setOpenAccessoryPopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeleteAccessory: (arg: string) => Promise<string>
}

const AccessoriesTable: React.FC<IProps> = ({
  accessoryRecords,
  handleOpenDetails,
  setOpenAccessoryPopup,
  setEditMode,
  fetching,
  handleDeleteAccessory
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')
  const [filteredRecords, setFilteredRecords] = useState<IAccessory[]>(accessoryRecords)

  useEffect(() => {
    setFilteredRecords(accessoryRecords)
  }, [accessoryRecords])

  const handleAddAccessory = () => {
    setEditMode(false)
    setOpenAccessoryPopup(true)
  }

  const handleEditAccessory = (id: string | undefined) => {
    if (id) {
      setEditMode(true)
      setOpenAccessoryPopup(true)
      handleOpenDetails(id)
    }
  }

  const handleDeleteRecord = async (id: string | undefined) => {
    if (id) {
      const message = await handleDeleteAccessory(id)
      if (message === 'SUCCESSFULL') {
        toast.success('Aksesuar basariyla silindi')
      }
    }
  }

  const handleShowModalDelete = (id: string | undefined) => {
    if (id) {
      setShowModalDelete(true)
      setSelectedId(id)
    }
  }

  const handleDelete = () => {
    handleDeleteRecord(selectedId)
    setShowModalDelete(false)
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredRecords(accessoryRecords)
    } else {
      const filtered = accessoryRecords.filter((acc) =>
        acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (acc.category || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredRecords(filtered)
    }
  }, 300)

  const handleInputChange = (e: { target: { value: string } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-accessory'
                  className='form-control search'
                  placeholder='Aksesuar veya kategori ara...'
                  onChange={handleInputChange}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              <Button className='p-2' color='primary' onClick={handleAddAccessory}>
                <div className='flex align-items-center'>
                  {fetching ? (
                    <Spinner size='sm'> Loading... </Spinner>
                  ) : (
                    <>
                      <i className='mdi mdi-plus'></i>
                      <span className='fw-semibold ms-1'>Yeni Aksesuar</span>
                    </>
                  )}
                </div>
              </Button>
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card'>
                <table className='table align-middle table-nowrap table-striped-columns mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col' style={{ width: '80px' }}>Onizleme</th>
                      <th scope='col'>Isim</th>
                      <th scope='col'>Aktif Booth Sayisi</th>
                      <th scope='col' style={{ width: '150px' }}>Islemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((accessory, index) => (
                      <tr key={index}>
                        <td>
                          <img
                            src={getAssetUrl(accessory.imageLink)}
                            alt={accessory.name}
                            style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://placehold.co/60x40?text=No+Image'
                            }}
                          />
                        </td>
                        <td>
                          <Link to='#' className='fw-medium'>
                            {accessory.name}
                          </Link>
                        </td>
                        <td>
                          <span className='badge bg-info'>
                            {accessory.isActiveFor?.length || 0} booth
                          </span>
                        </td>
                        <td>
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => handleEditAccessory(accessory?._id)}
                          >
                            Duzenle
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-danger ms-2'
                            onClick={() => handleShowModalDelete(accessory?._id)}
                          >
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredRecords.length === 0 && !fetching && (
                      <tr>
                        <td colSpan={5} className='text-center py-4'>
                          Aksesuar bulunamadi
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowModalDelete(false)}
      />
    </React.Fragment>
  )
}

export default AccessoriesTable
